// Das HUD der Ego-Sicht: Fadenkreuz, Hinweis auf die verfügbare Handlung („Melken [E]“), die
// Leiste mit den Stapeln des Inventars und eine kleine Anzeige, ob die Welt-Uhr verbunden ist.
// Alles hier ist Ansicht (Architektur-Regel 7): Das Inventar bleibt das Datenmodell in `shared`,
// die Leiste zeigt es nur. Die reinen Funktionen (`hintText`, `hotbarSlots`, `itemSymbol`,
// `connectionText`) sind getestet; `createHud` hängt das DOM an und rührt es nur an, wenn sich
// etwas geändert hat, damit der Aufruf je Bild nichts kostet. Stile liegen als Klassen in
// `index.html` (ADR 0011, Punkt 11). Einzelheiten in ADR 0012.
//
// Seit task-015 steht oben links die Zeit: die Tageszeit der Welt und daneben der eigene Hoftag,
// der nur zählt, solange man da ist (ADR 0029).

import { ITEMS, LEISTE_FAECHER, farmDay, type Inventory, type ItemId } from '@bauernhof/shared';
import { keyLabel } from '../input/keys.js';

// `keyLabel` wohnt in input/keys.ts, weil das Overlay die Belegung damit beschriftet; das HUD
// reicht es weiter, damit Hinweis und Overlay dieselbe Schreibweise zeigen.
export { keyLabel };

/** Was der Hinweis braucht; ein `Action` aus `shared` passt strukturell. */
export interface HudAction {
  label: string;
  key: string;
}

export interface Hud {
  /** Handlung im Blick, oder null: dann verschwindet der Hinweis. */
  setHint(action: HudAction | null): void;
  /** Zeigt die Stapel des Inventars in der Leiste; darf je Bild aufgerufen werden. */
  setInventory(inv: Inventory): void;
  /** Zustand der Welt-Uhr; vor dem ersten Aufruf ist die Anzeige unsichtbar. */
  setConnection(connected: boolean): void;
  /**
   * Blendet einen kurzen Satz unter dem Hinweis ein, etwa die Ablehnung des Servers („Das ist zu
   * weit weg.“); er verschwindet nach `MELDUNG_DAUER_MS` von selbst. Ein neuer Satz ersetzt den alten.
   */
  zeigeMeldung(text: string): void;
  /**
   * Tageszeit der Welt (`dayFraction`) und Stand der eigenen Hof-Uhr in Hof-Sekunden; darf je Bild
   * aufgerufen werden. Vor dem ersten Aufruf ist die Anzeige unsichtbar.
   */
  setZeit(tagesanteil: number, farmSeconds: number): void;
  /**
   * Der eigene Satz über der Leiste, solange er gilt – für die Ego-Sicht, in der man die Blase über dem
   * eigenen Kopf nicht sieht (task-016); null blendet ihn aus. Darf je Bild aufgerufen werden.
   */
  setEigenerSatz(text: string | null): void;
  /** Entfernt alle Elemente des HUD wieder. */
  dispose(): void;
}

/** Minuten eines Tages der Welt. */
const MINUTEN_JE_TAG = 24 * 60;
/** Die Uhrzeit springt in diesen Schritten, damit die Anzeige nicht jede Sekunde flackert. */
const UHR_SCHRITT_MINUTEN = 10;

/**
 * Die Tageszeit der Welt als Uhrzeit, auf zehn Minuten abgerundet: „7:40 Uhr“. `tagesanteil` ist
 * `dayFraction` aus shared (0 = Mitternacht, 0.5 = Mittag); Unbrauchbares gilt als Mitternacht.
 */
export function tageszeitText(tagesanteil: number): string {
  const anteil = Number.isFinite(tagesanteil) ? Math.min(Math.max(tagesanteil, 0), 1) : 0;
  // Das kleine Epsilon fängt Rundungsreste ab, damit 7:50 nicht als 7:49,999 zu 7:40 wird.
  const schritte = Math.floor((anteil * MINUTEN_JE_TAG) / UHR_SCHRITT_MINUTEN + 1e-9);
  const minuten = (schritte * UHR_SCHRITT_MINUTEN) % MINUTEN_JE_TAG;
  const stunde = Math.floor(minuten / 60);
  return `${stunde}:${String(minuten % 60).padStart(2, '0')} Uhr`;
}

/** Der eigene Satz in der Ego-Sicht, wo man die Blase über dem eigenen Kopf nicht sieht (task-016). */
export function eigenerSatzText(text: string): string {
  return `Du sagst: „${text}“`;
}

/** Der eigene Hoftag, ab 1 gezählt: „Hoftag 3“. */
export function hoftagText(farmSeconds: number): string {
  return `Hoftag ${farmDay(farmSeconds)}`;
}

/**
 * Fächer in der Leiste. Mehr Stapel als Fächer werden nicht gezeigt (siehe `hotbarSlots`). Die Zahl
 * kommt aus shared: Der Server nimmt beim Umlegen aus der Kiste keine Art an, für die kein Fach frei
 * wäre (task-014).
 */
export const HOTBAR_SLOTS = LEISTE_FAECHER;

/** So lange steht eine Meldung im Bild; lang genug zum Lesen, kurz genug, um nicht zu stören. */
export const MELDUNG_DAUER_MS = 2500;

/** Ein Fach der Leiste: leer (`item: null`, `count: 0`) oder ein Stapel. */
export interface HotbarSlot {
  item: ItemId | null;
  count: number;
}

/** Symbol eines Gegenstands: ein Bild aus der Tabelle oder ersatzweise ein Buchstabe. */
export type ItemSymbol =
  | { kind: 'svg'; markup: string; name: string }
  | { kind: 'letter'; text: string; name: string };

/** Der Hinweis unter dem Fadenkreuz, z. B. `Melken [E]`; leer ohne Handlung. */
export function hintText(action: HudAction | null): string {
  if (action === null) return '';
  return `${action.label} [${keyLabel(action.key)}]`;
}

/**
 * Die Fächer der Leiste aus dem Inventar: Stapel in ihrer Reihenfolge (erstes Auftauchen, siehe
 * `Inventory.stacks`), dahinter leere Fächer bis `slotCount`. Sind es mehr Stapel als Fächer,
 * werden die überzähligen abgeschnitten und nicht gezeigt; in Version 1 gibt es ein Produkt,
 * ein Blättern oder Rucksack kommt mit einem eigenen Task. Liefert Kopien, verändert nichts.
 */
export function hotbarSlots(inv: Inventory, slotCount: number = HOTBAR_SLOTS): HotbarSlot[] {
  const anzahl = Math.max(0, Math.floor(slotCount));
  const faecher: HotbarSlot[] = [];
  for (let i = 0; i < anzahl; i++) {
    const stapel = inv.stacks[i];
    faecher.push(stapel === undefined ? { item: null, count: 0 } : { item: stapel.item, count: stapel.count });
  }
  return faecher;
}

/**
 * Bilder je Symbolkennung (`ItemDef.icon`), als Inline-SVG ohne externes Laden. Der Milchkrug ist
 * ein Steinkrug mit Henkel, wie er um 1800 auf dem Hof stand. Ein Gegenstand ohne Bild bekommt
 * seinen Anfangsbuchstaben (ADR 0003: Ersatzbild statt Fehler).
 */
const ICON_SVG: Readonly<Record<string, string>> = {
  milch:
    '<svg viewBox="0 0 32 32" aria-hidden="true">' +
    '<path d="M22 12h4a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-4" fill="none" stroke="#f4ecd8" stroke-width="2.5" stroke-linecap="round"/>' +
    '<path d="M9 9h14v16a3 3 0 0 1-3 3h-8a3 3 0 0 1-3-3z" fill="#f4ecd8"/>' +
    '<path d="M9 9L5 4l8 3z" fill="#f4ecd8"/>' +
    '<rect x="9" y="18" width="14" height="3" fill="#4a7bb5"/>' +
    '<rect x="9" y="7" width="14" height="3" rx="1" fill="#ffffff"/>' +
    '</svg>',
  // Ein roter Apfel mit Stiel, Blatt und einem hellen Glanz (task-014).
  apfel:
    '<svg viewBox="0 0 32 32" aria-hidden="true">' +
    '<path d="M16 10c-3-2.6-9.5-2-10.4 4.2-.8 5.4 2.4 12.6 6.4 13.6 1.6.4 2.6-.5 4-.5s2.4.9 4 .5c4-1 7.2-8.2 6.4-13.6C25.5 8 19 7.4 16 10z" fill="#c8321f"/>' +
    '<path d="M11.5 13c-1.8 1-2.8 2.9-2.8 5" fill="none" stroke="#f29a86" stroke-width="1.8" stroke-linecap="round"/>' +
    '<path d="M16 10c0-2.2.6-4.2 2-5.6" fill="none" stroke="#5a3a1e" stroke-width="2" stroke-linecap="round"/>' +
    '<path d="M18.2 6.4c1.8-2.2 4.8-2.6 6.4-1.6-.8 2.2-3.8 3.4-6.4 1.6z" fill="#4f8a2e"/>' +
    '</svg>',
};

/** Das Symbol zu einem Gegenstand samt Anzeigename (aus `ITEMS`, sonst die Kennung selbst). */
export function itemSymbol(item: ItemId): ItemSymbol {
  const def = ITEMS[item];
  const name = def?.name ?? item;
  const markup = def === undefined ? undefined : ICON_SVG[def.icon];
  if (markup !== undefined) return { kind: 'svg', markup, name };
  const buchstabe = item.charAt(0).toUpperCase();
  return { kind: 'letter', text: buchstabe === '' ? '?' : buchstabe, name };
}

/** Beschriftung der Verbindungsanzeige. */
export function connectionText(connected: boolean): string {
  return connected ? 'Uhr: verbunden' : 'Uhr: getrennt';
}

/** Hängt Fadenkreuz, Hinweis, Leiste und Verbindungsanzeige an `root` (in der Regel `document.body`). */
export function createHud(root: HTMLElement): Hud {
  const fadenkreuz = element('div', 'hud-fadenkreuz');
  fadenkreuz.setAttribute('aria-hidden', 'true');

  const hinweis = element('div', 'hud-hinweis');
  hinweis.hidden = true;

  const leiste = element('div', 'hud-leiste');
  leiste.setAttribute('aria-label', 'Inventar');
  const faecher: FachAnsicht[] = [];
  for (let i = 0; i < HOTBAR_SLOTS; i++) {
    const fach = fachAnsicht();
    faecher.push(fach);
    leiste.append(fach.wurzel);
  }

  const uhr = element('div', 'hud-uhr');
  uhr.hidden = true;

  const meldung = element('div', 'hud-meldung');
  meldung.hidden = true;
  meldung.setAttribute('role', 'status');

  // Tageszeit und Hoftag nebeneinander (task-015); oben links, fern von Bildrate und Uhr-Zustand.
  const zeit = element('div', 'hud-zeit');
  zeit.hidden = true;
  const tageszeit = element('span', 'hud-zeit-tag');
  const hoftag = element('span', 'hud-zeit-hoftag');
  zeit.append(tageszeit, hoftag);

  // Der eigene Satz über der Leiste (task-016).
  const satz = element('div', 'hud-satz');
  satz.hidden = true;

  root.append(fadenkreuz, hinweis, meldung, leiste, uhr, zeit, satz);

  let letzterHinweis = '';
  let letzteFaecher: HotbarSlot[] = faecher.map(() => ({ item: null, count: 0 }));
  let letzteVerbindung: boolean | null = null;
  let meldungTimer: ReturnType<typeof setTimeout> | null = null;
  let letzteTageszeit = '';
  let letzterHoftag = '';
  let letzterSatz: string | null = null;

  return {
    setHint(action) {
      const text = hintText(action);
      if (text === letzterHinweis) return;
      letzterHinweis = text;
      hinweis.textContent = text;
      hinweis.hidden = text === '';
    },
    setInventory(inv) {
      const neu = hotbarSlots(inv, faecher.length);
      neu.forEach((fach, i) => {
        const alt = letzteFaecher[i];
        const ansicht = faecher[i];
        if (ansicht === undefined) return;
        if (alt !== undefined && alt.item === fach.item && alt.count === fach.count) return;
        if (alt?.item !== fach.item) ansicht.setItem(fach.item);
        ansicht.setCount(fach.count);
      });
      letzteFaecher = neu;
    },
    setConnection(connected) {
      if (connected === letzteVerbindung) return;
      letzteVerbindung = connected;
      uhr.textContent = connectionText(connected);
      uhr.classList.toggle('hud-uhr-getrennt', !connected);
      uhr.hidden = false;
    },
    zeigeMeldung(text) {
      if (meldungTimer !== null) clearTimeout(meldungTimer);
      meldung.textContent = text;
      meldung.hidden = false;
      meldungTimer = setTimeout(() => {
        meldung.hidden = true;
        meldungTimer = null;
      }, MELDUNG_DAUER_MS);
    },
    setZeit(tagesanteil, farmSeconds) {
      const text = tageszeitText(tagesanteil);
      if (text !== letzteTageszeit) {
        letzteTageszeit = text;
        tageszeit.textContent = text;
      }
      const tag = hoftagText(farmSeconds);
      if (tag !== letzterHoftag) {
        letzterHoftag = tag;
        hoftag.textContent = tag;
      }
      zeit.hidden = false;
    },
    setEigenerSatz(text) {
      if (text === letzterSatz) return;
      letzterSatz = text;
      satz.textContent = text === null ? '' : eigenerSatzText(text);
      satz.hidden = text === null;
    },
    dispose() {
      if (meldungTimer !== null) clearTimeout(meldungTimer);
      for (const teil of [fadenkreuz, hinweis, meldung, leiste, uhr, zeit, satz]) teil.remove();
    },
  };
}

/** Ein Fach der Leiste als DOM: Symbol in der Mitte, Anzahl rechts unten. */
export interface FachAnsicht {
  wurzel: HTMLElement;
  setItem(item: ItemId | null): void;
  setCount(count: number): void;
}

/** Ein Fach; die Kistenansicht nimmt dasselbe als Knopf, damit es sich anklicken lässt (task-014). */
export function fachAnsicht(tag: 'div' | 'button' = 'div'): FachAnsicht {
  const wurzel = element(tag, 'hud-fach hud-fach-leer');
  if (wurzel instanceof HTMLButtonElement) wurzel.type = 'button';
  const symbol = element('span', 'hud-fach-symbol');
  const anzahl = element('span', 'hud-fach-anzahl');
  wurzel.append(symbol, anzahl);
  return {
    wurzel,
    setItem(item) {
      if (item === null) {
        symbol.replaceChildren();
        wurzel.classList.add('hud-fach-leer');
        wurzel.removeAttribute('title');
        return;
      }
      const bild = itemSymbol(item);
      if (bild.kind === 'svg') {
        // Feste Markup-Konstante aus diesem Modul, keine fremden Daten.
        symbol.innerHTML = bild.markup;
      } else {
        symbol.textContent = bild.text;
      }
      wurzel.classList.remove('hud-fach-leer');
      wurzel.title = bild.name;
    },
    setCount(count) {
      anzahl.textContent = count > 0 ? String(count) : '';
    },
  };
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  el.className = className;
  return el;
}
